import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
  FormDescription,
} from 'src/shadcn/components/ui/form';
import { Input } from 'src/shadcn/components/ui/input';
import { Button } from 'src/shadcn/components/ui/button';
import { CalendarIcon, RotateCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PhoneInput } from 'src/shadcn/components/ui/PhoneInput';
import { Textarea } from 'src/shadcn/components/ui/textarea';
import { useRequestAppointment } from '../lib/useRequestAppointment';
import { CloudFlareTurnstileComponent } from 'src/Components/CloudFlareTurnstileComponent';
import { useTurnstile } from 'react-turnstile';
import { AppointmentDatePicker } from './AppointmentDatePicker';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
} from 'src/shadcn/components/ui/alert-dialog';

export interface IRequestAppointmentFormProps {
  onRequestAppointment: ReturnType<
    typeof useRequestAppointment
  >['onRequestAppointment'];
  isRequestingAppointment: boolean;
  onRequestSuccessful: () => void;
}

export const RequestAppointmentForm: React.FunctionComponent<
  IRequestAppointmentFormProps
> = props => {
  const { form } = useRequestAppointmentForm(props);
  const turnstile = useTurnstile();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [pendingData, setPendingData] = React.useState<any>(null);

  function handleFormSubmit(data: any) {
    props.onRequestAppointment(
      {
        customerFirstName: data.firstName,
        customerLastName: data.lastName,
        customerPhoneNumber: data.phoneNumber,
        notes: data.notes,
        requestedAppointmentTime: data.requestedDate,
      },
      data.cloudflareToken,
      () => {
        props.onRequestSuccessful();
        turnstile.reset();
      }
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={e => {
          e.preventDefault();
          form.handleSubmit(data => {
            setPendingData(data);
            setDialogOpen(true);
          })(e);
        }}
        className='space-y-10'
      >
        <FormField
          control={form.control}
          name='phoneNumber'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem className='flex flex-col items-start'>
              <FormLabel className='text-left'>Phone Number</FormLabel>
              <FormControl className='w-full'>
                <PhoneInput
                  defaultCountry='CA'
                  placeholder='Enter a phone number'
                  {...field}
                />
              </FormControl>
              <FormDescription className='text-left'>
                We will contact you on this number to confirm your appointment.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='firstName'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem>
              <FormLabel>First name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='lastName'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='email'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='requestedDate'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem className='flex flex-col'>
              <FormLabel>Requested appointment date</FormLabel>
              <AppointmentDatePicker
                fieldValue={field.value}
                setFieldValue={field.onChange}
              />
              <FormDescription>
                We will do our best to accomodate your requested date.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='notes'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Tell us about the vehicle? Make, model, year, and reason for
                appointment.
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder='Tell us about the vehicle you would like to get fixed and what the issue with it is.'
                  className='resize-none'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='cloudflareToken'
          disabled={props.isRequestingAppointment}
          render={({ field }) => (
            <FormItem>
              <CloudFlareTurnstileComponent
                onVerify={token => {
                  field.onChange(token);
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type='submit' disabled={!form.formState.isDirty}>
          <CalendarIcon className='mr-2 h-4 w-4' />
          <span>Request appointment</span>
          {props.isRequestingAppointment && (
            <RotateCw className='ml-2 h-4 w-4 animate-spin' />
          )}
        </Button>
      </form>
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Appointment Request</AlertDialogTitle>
          </AlertDialogHeader>
          <div>
            <ul className='list-disc space-y-2 pl-6 text-sm'>
              <li>
                Please wait for confirmation before bringing your vehicle in.
              </li>
              <li>
                A member of our team will contact you to confirm your
                appointment shortly.
              </li>
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type='button' variant='outline'>
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type='button'
                onClick={() => {
                  setDialogOpen(false);
                  if (pendingData) {
                    handleFormSubmit(pendingData);
                    setPendingData(null);
                  }
                }}
              >
                Please request appointment
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
};

function useRequestAppointmentForm(props: IRequestAppointmentFormProps) {
  const form = useForm({
    resolver: zodResolver(
      z.object({
        requestedDate: z.date({
          required_error: 'Please enter requested date for appointment',
        }),
        firstName: z.string().min(1, { message: 'Please enter a first name' }),
        lastName: z.string().min(1, { message: 'Please enter a last name' }),
        phoneNumber: z.string().refine(isValidPhoneNumber, {
          message: 'Please enter a valid phone number',
        }),
        email: z.string().nullish(),
        notes: z.string(),
        cloudflareToken: z
          .string()
          .min(1, { message: 'Please click on the captcha to verify.' }),
      })
    ),
    defaultValues: {
      requestedDate: new Date(),
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      notes: '',
      cloudflareToken: '',
    },
  });
  return { form };
}
